import React from 'react';

import InfoIcon from 'icons/info.svg';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import ActionsPill from 'components/application/ActionsPill';
import ErrorMessage from 'components/application/ErrorMessage';

import BookingCourseDetails from 'components/bookings/BookingCourseDetails';
import BookingDetailsForm from 'components/bookings/BookingDetailsForm';
import SelectedMembersList from 'components/bookings/SelectedMembersList';
import SelectMembers from 'components/bookings/SelectMembers';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function BookingConnectionsSidePanelForm({
  currentBooking,
  domainBookingSource,
  domainCourse,
  domainPersonnelCollection,
  isELearningAllowanceError,
  isFetching,
  onBookingGroupMemberDelete,
  onBookingGroupMemberSelect,
  onDateChange,
  onInputChange,
  remainingCredits,
  removeErrorStyling,
  requestError,
  showBookingGroupView,
}) {
  const { hasBookingGroupEditableAccess } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'bookings.BookingConnectionsSidePanelForm',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const courseHasELearningCourse =
    !!domainCourse?.relationships?.eLearningCourse?.data?.id;

  return (
    <>
      {showBookingGroupView && (
        <>
          <div className='popup__body-content'>
            <div className='tw-mb-4 tw-text-l tw-font-semibold tw-text-grey-700'>
              {i18nTCommon('courses.courseDetails')}
            </div>
            <BookingCourseDetails course={domainCourse} />
          </div>
          <hr className='tw-my-0 tw-h-px tw-border-0 tw-bg-grey-100' />
        </>
      )}
      <div className='popup__body-form--light'>
        {showBookingGroupView && (
          <div className='tw-mb-4 tw-text-l tw-font-semibold tw-text-grey-700'>
            {courseHasELearningCourse ?
              i18nTCommon('bookings.enrolmentDetails')
            : i18nTCommon('bookings.bookingDetails')}
          </div>
        )}
        <div className='tw-m-0 tw-mb-5 tw-flex tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
          <div className='tw-mr-3 tw-h-5'>
            <InfoIcon
              className='[&_path]:tw-fill-cyan-800'
              height={20}
              width={20}
            />
          </div>
          <p className='tw-m-0'>
            {i18nTCommon(
              'personnel.emailNotifications.personnelAndLineManager',
            )}
          </p>
        </div>
        <div className='tw-mb-6'>
          <ErrorMessage
            isFallback={requestError.isFallback}
            modifiers='side-panel__alert side-panel__alert--danger side-panel__alert--wide'
            validationErrors={requestError.validationErrors}
          />
        </div>
        {!courseHasELearningCourse && (
          <BookingDetailsForm
            currentBooking={currentBooking}
            dateLabel={i18nTCommon('training.trainingDate')}
            onDateChange={onDateChange}
            onInputChange={onInputChange}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
          />
        )}
        {hasBookingGroupEditableAccess &&
          domainBookingSource?.type === 'booking' &&
          !showBookingGroupView && (
            <div className='tw-mt-4 tw-flex tw-gap-3 tw-rounded-lg tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
              <div className='tw-h-5'>
                <InfoIcon
                  className='[&_path]:tw-fill-cyan-800'
                  height={20}
                  width={20}
                />
              </div>
              <p className='tw-mb-0'>
                <Trans
                  components={{
                    a: (
                      /* eslint-disable jsx-a11y/anchor-has-content */
                      <a
                        className='app-link tw-align-baseline tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                        href='/personnel?tab=bookings'
                      />
                      /* eslint-enable jsx-a11y/anchor-has-content */
                    ),
                  }}
                  i18nKey='bookings.BookingConnectionsSidePanelForm.addMorePersonnel'
                  ns='components'
                />
              </p>
            </div>
          )}
      </div>
      {showBookingGroupView && (
        <>
          <hr className='tw-my-0 tw-h-px tw-border-0 tw-bg-grey-100' />
          <div className='popup__body-form--light'>
            <div className='tw-mb-4 tw-flex tw-justify-between'>
              <div className='tw-flex tw-items-center tw-gap-2'>
                <div className='tw-text-l tw-font-semibold tw-text-grey-700'>
                  {i18nTCommon('shared.personnel')}
                </div>
                {currentBooking.personnelIds.length > 0 && (
                  <div className='tw-flex tw-h-5 tw-items-center tw-justify-center tw-rounded-lg tw-bg-grey-050 tw-px-1.5 tw-text-center'>
                    <p className='tw-m-0 tw-font-inter tw-text-xs tw-font-semibold'>
                      {currentBooking.personnelIds.length}
                    </p>
                  </div>
                )}
              </div>
              <div className='tw-flex'>
                {remainingCredits !== null && remainingCredits <= 25 && (
                  <ActionsPill
                    color={
                      remainingCredits > 0 ? 'grey'
                      : remainingCredits === 0 ?
                        'amber'
                      : 'red'
                    }
                    displayText={i18nT('remainingCredits', {
                      remainingCredits,
                    })}
                  />
                )}
              </div>
            </div>
            <SelectMembers
              domainBookingSource={domainBookingSource}
              domainCourse={domainCourse}
              domainPersonnelCollection={domainPersonnelCollection}
              isELearningAllowanceError={isELearningAllowanceError}
              isFetching={isFetching}
              onBookingGroupMemberSelect={onBookingGroupMemberSelect}
              selectedMemberIds={currentBooking.personnelIds}
            />
          </div>
          <div className='popup__body-form -tw-mt-5 tw-mb-8 tw-pt-0'>
            <SelectedMembersList
              domainPersonnelCollection={domainPersonnelCollection}
              isFetching={isFetching}
              onBookingGroupMemberDelete={onBookingGroupMemberDelete}
              selectedMemberIds={currentBooking.personnelIds}
            />
          </div>
        </>
      )}
    </>
  );
}

BookingConnectionsSidePanelForm.propTypes = {
  currentBooking: PropTypes.shape({
    date: PropTypes.instanceOf(Date),
    notes: PropTypes.string,
    personnelIds: PropTypes.array,
  }).isRequired,
  domainBookingSource: resourceShape(['booking', 'bookingGroup']),
  domainPersonnelCollection: PropTypes.arrayOf(resourceShape('personnel')),
  domainCourse: resourceShape('course').isRequired,
  isFetching: PropTypes.bool,
  onBookingGroupMemberSelect: PropTypes.func,
  onBookingGroupMemberDelete: PropTypes.func,
  onDateChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  remainingCredits: PropTypes.number,
  removeErrorStyling: PropTypes.func.isRequired,
  requestError: PropTypes.object,
  showBookingGroupView: PropTypes.bool,
  isELearningAllowanceError: PropTypes.bool,
};
